import { useState } from 'react';
import { Alert, Button, Checkbox, SearchSelect, SelectField, TextField } from '@dru/ui';
import { api } from '../lib/api';
import { useQuery } from '../lib/useQuery';
import { errorMessage } from '../lib/auth';

type Configuration = { id: string; name: string; status: string; scope: string; configVersion: number };
type Test = { id: string; name: string; code: string };
export function ClinicalCompanyConfiguration({ orderId }: { orderId: string }) {
  const [open, setOpen] = useState(false);
  return <details onToggle={e => setOpen(e.currentTarget.open)} className="rounded-lg border p-4">
    <summary className="cursor-pointer font-semibold">Company screening configuration — clinical review</summary>
    {open && <ConfigurationReview orderId={orderId} />}
  </details>;
}
function ConfigurationReview({ orderId }: { orderId: string }) {
  const base = `/clinical-company-configuration/orders/${orderId}`;
  const configurations = useQuery<{ items: Configuration[] }>(() => api.get(base), [base]);
  const tests = useQuery<Test[]>(() => api.get('/occupational-baselines/tests'), []);
  const scopes = useQuery<{ jobRoles: Array<{ id: string; title: string }> }>(() => api.get(`${base}/scopes`), [base]);
  const [name, setName] = useState('');
  const [scope, setScope] = useState('COMPANY');
  const [roleId, setRoleId] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<{ id: string; name: string; tests: Test[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function act(fn: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await fn(); configurations.reload(); } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }
  return <div className="space-y-3 mt-3">
    <p className="text-caption">Changes apply to future examinations for this company. Existing orders retain their frozen battery. Mandatory baseline and hazard tests remain required.</p>
    {(error || configurations.error) && <Alert tone="danger">{error ?? configurations.error}</Alert>}
    {configurations.data?.items.map(c => <div key={c.id} className="border-b py-2 flex flex-wrap justify-between gap-2">
      <span>{c.name} · {c.scope} · {c.status} · v{c.configVersion}</span>
      {c.status === 'DRAFT' && <Button size="sm" variant="secondary" loading={busy} onClick={() => act(async () => setPreview(await api.get(`${base}/${c.id}`)))}>Review draft</Button>}
    </div>)}
    {preview && <div className="border p-3 space-y-2"><strong>{preview.name}</strong><ul>{preview.tests.map(t => <li key={t.id}>{t.name} ({t.code})</li>)}</ul>
      <Button loading={busy} onClick={() => act(async () => { await api.post(`${base}/${preview.id}/publish`, {}); setPreview(null); })}>Approve and publish</Button>
      <Button variant="secondary" onClick={() => setPreview(null)}>Close</Button>
    </div>}
    <details><summary className="cursor-pointer">Create a clinical test configuration</summary><div className="space-y-3 mt-3">
      <TextField label="Configuration name" value={name} onChange={setName} />
      <SelectField label="Applies to" value={scope} onChange={setScope}><option value="COMPANY">Company default</option><option value="ROLE">Job role</option></SelectField>
      {scope === 'ROLE' && <SearchSelect label="Job role" value={roleId} onChange={setRoleId} placeholder="Choose job role" searchPlaceholder="Search job roles…" options={(scopes.data?.jobRoles ?? []).map(r => ({ value: r.id, label: r.title }))} />}
      <TextField label="Find tests" value={search} onChange={setSearch} />
      <div className="max-h-56 overflow-auto space-y-2">{tests.data?.filter(t => `${t.code} ${t.name}`.toLowerCase().includes(search.toLowerCase())).map(t => <Checkbox key={t.id} label={`${t.name} (${t.code})`} checked={selected.has(t.id)} onChange={checked => setSelected(old => {
        const next = new Set(old); if (checked) next.add(t.id); else next.delete(t.id); return next;
      })} />)}</div>
      <Button loading={busy} disabled={name.trim().length < 2 || !selected.size || (scope === 'ROLE' && !roleId)} onClick={() => act(async () => {
        await api.post(base, { name, packageIds: [], addTestIds: [...selected],
          ...(scope === 'ROLE' ? { jobRoleId: roleId } : {}) });
        setName(''); setSelected(new Set());
      })}>Save for clinical review</Button>
    </div></details>
  </div>;
}
